import { dataService } from '@/services/dataService';
import { ENV } from '@/config/env';
import { DATA_MODE } from '@/config/dataMode';
import { getStatusMeta } from '@/config/statuses.config';
import { AI_PROVIDERS } from '@/config/aiProviders.config';

/**
 * DIAGNOSTICS — Phase D §16. Every field here is something this frontend
 * can genuinely observe right now. Anything it cannot verify (backend
 * process health, database connectivity beyond what /api reports, disk,
 * memory) is reported as UNKNOWN / REQUIRES BACKEND CHECK — never
 * upgraded to "Healthy" without real evidence.
 *
 * i18n: `label` is never raw English — every row carries a `labelKey`
 * (always a translation key, optionally suffixed with a non-translatable
 * data-layer fact like a system service's displayName via `labelSuffix`,
 * the same way SystemPage.tsx shows `s.displayName` untranslated). `value`
 * is either a raw non-translatable fact (a version string, a route, a
 * count, a browser UA) or itself an i18n key when `valueIsKey` is true —
 * the page resolves it through `t()` before rendering.
 */
export type DiagnosticTone = 'good' | 'warn' | 'crit' | 'neutral';

export interface DiagnosticRow {
  labelKey: string;
  /** non-translatable raw context appended after the translated label, e.g. a system service's displayName */
  labelSuffix?: string;
  value: string;
  /** true when `value` is itself a translation key to resolve via t(), rather than a raw fact */
  valueIsKey?: boolean;
  tone: DiagnosticTone;
}

export interface DiagnosticReport {
  rows: DiagnosticRow[];
  issueCount: number;
}

export async function runDiagnostics(context: { route: string; lang: string; theme: string }): Promise<DiagnosticReport> {
  const [systemServices, connectors, agents] = await Promise.all([
    dataService.listSystemServices(),
    dataService.listConnectorStatuses(),
    dataService.listAgents(),
  ]);

  const rows: DiagnosticRow[] = [
    { labelKey: 'diagnostics.labels.frontendLoad', value: 'diagnostics.values.frontendLoadOk', valueIsKey: true, tone: 'good' },
    { labelKey: 'diagnostics.labels.currentRoute', value: context.route, tone: 'neutral' },
    { labelKey: 'diagnostics.labels.appVersion', value: ENV.version, tone: 'neutral' },
    { labelKey: 'diagnostics.labels.environment', value: `env.${ENV.environment}`, valueIsKey: true, tone: 'neutral' },
    { labelKey: 'diagnostics.labels.dataMode', value: DATA_MODE, tone: 'neutral' },
    { labelKey: 'diagnostics.labels.browser', value: typeof navigator !== 'undefined' ? navigator.userAgent : 'UNKNOWN', tone: 'neutral' },
    { labelKey: 'diagnostics.labels.language', value: context.lang, tone: 'neutral' },
    { labelKey: 'diagnostics.labels.theme', value: context.theme, tone: 'neutral' },
  ];

  for (const s of systemServices) {
    const meta = getStatusMeta('system', s.status);
    // A system row is honestly verifiable from the browser ONLY for the
    // frontend's own "this page rendered" fact. Every other row (backend
    // API, database, workflow engine, ...) genuinely cannot be confirmed
    // from client-side code alone unless the app is wired to a live
    // backend — so in demo mode, every non-frontend row is reported as
    // REQUIRES BACKEND CHECK regardless of what listSystemServices()
    // returned, rather than repeating a status this frontend can't prove.
    const isFrontendSelf = s.key === 'frontend';
    const isHonestlyUnverifiable = DATA_MODE !== 'live' && !isFrontendSelf;
    const requiresBackendCheck = s.status === 'not_connected' || isHonestlyUnverifiable;
    rows.push({
      labelKey: 'diagnostics.labels.system',
      labelSuffix: s.displayName,
      value: requiresBackendCheck ? 'diagnostics.requiresBackendCheck' : meta.labelKey,
      valueIsKey: true,
      tone: requiresBackendCheck ? 'neutral' : meta.tone === 'good' ? 'good' : meta.tone === 'crit' ? 'crit' : meta.tone === 'warn' ? 'warn' : 'neutral',
    });
  }

  const connectorIssues = connectors.filter((c) => getStatusMeta('connector', c.status).tone === 'crit' || getStatusMeta('connector', c.status).tone === 'warn');
  rows.push({ labelKey: 'diagnostics.labels.connectorsTotal', value: String(connectors.length), tone: 'neutral' });
  rows.push({ labelKey: 'diagnostics.labels.connectorsWithProblem', value: String(connectorIssues.length), tone: connectorIssues.length > 0 ? 'warn' : 'good' });

  rows.push({ labelKey: 'diagnostics.labels.configuredAgents', value: String(agents.filter((a) => !a.archived).length), tone: 'neutral' });

  const configuredProviders = Object.values(AI_PROVIDERS).filter((p) => p.configured).length;
  rows.push({ labelKey: 'diagnostics.labels.aiProvidersConnected', value: `${configuredProviders}/${Object.keys(AI_PROVIDERS).length}`, tone: configuredProviders > 0 ? 'good' : 'warn' });

  const issueCount = rows.filter((r) => r.tone === 'warn' || r.tone === 'crit').length;
  return { rows, issueCount };
}
